/**
 * Build step 1 — login, roles, clients, people, SPOC, audit trail (ADR-001 §11.3).
 * Every endpoint below is registered with `atlas contract` (owner backend-development, consumer ui-development).
 */
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders, page } from './common.js';
import { DevSignInBody, GoogleCallbackQuery, GoogleStartQuery, PasswordChangeBody, PasswordSignInBody } from './auth.js';
import { MeResponse } from './me.js';
import { CaptureTokenResponse, CreateUserBody, CreateUserResponse, ListUsersQuery, PasswordResetResponse, UpdateUserBody, User, UserCapabilityParams, UserIdParams, UserPage, } from './users.js';
import { ChangeSpocBody, ChangeSpocResponse, Client, ClientContact, ClientContactList, ClientDetail, ClientDetailManager, ClientDetailMember, ClientEmailDomain, ClientEmailDomainList, ClientIdParams, ClientManager, ClientMember, ClientNotePage, ClientRowManager, ClientRowMember, ClientRowPage, ContactParams, CreateClientBody, CreateContactBody, CreateEmailDomainBody, EmailDomainParams, ListClientNotesQuery, ListClientsQuery, SpocHistory, UpdateClientBody, UpdateContactBody, } from './clients.js';
import { AuditPage, AuditPageMember, ListAuditQuery, RowHistoryParams, RowHistoryQuery } from './audit.js';
import { HealthResponse } from './system.js';
import { API_BASE, defineRoute } from './route.js';
const CLIENT_READ = ['clients.read.all', 'clients.read.own'];
const CLIENT_EDIT = ['clients.edit.all', 'clients.edit.own'];
const ETAG = { ETag: 'W/"<version>" of the returned resource' };
export const step1Routes = {
    // ---------------------------------------------------------------- auth
    'auth.googleStart': defineRoute({
        id: 'auth.googleStart',
        method: 'GET',
        path: `${API_BASE}/auth/google/start`,
        summary: 'Start Google sign-in',
        description: 'Redirects to Google (authorisation code + PKCE, hd=amplispot.com hint, state stored server-side for 10 minutes). ' +
            'Also sets a 10-minute sign-in binder cookie that ties the sign-in to this browser; only its SHA-256 is stored.',
        access: { auth: 'none', note: 'Anyone.' },
        query: GoogleStartQuery,
        success: {
            status: 302,
            description: 'Redirect to Google',
            headers: {
                Location: 'accounts.google.com URL',
                'Set-Cookie': '__Host-ops_oauth=<random>; HttpOnly; SameSite=Lax; Path=/; Max-Age=600; Secure (ops_oauth without Secure on plain-HTTP local runs)',
            },
        },
        errors: ['validation_failed', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'auth.googleCallback': defineRoute({
        id: 'auth.googleCallback',
        method: 'GET',
        path: `${API_BASE}/auth/google/callback`,
        summary: 'Finish Google sign-in',
        description: 'Consumes the single-use state and refuses (state_mismatch) unless the request carries the binder cookie set by ' +
            'the start call in the same browser, so a copied or planted callback URL signs no one in; the binder is cleared ' +
            'on every outcome. ' +
            'Verifies the ID token (iss, aud, exp, email_verified, hd claim AND email domain), looks up an active user by ' +
            'lower(email), creates a session and sets the ops_sid cookie, then redirects to the original `redirect` or the ' +
            "role's home route. Failures redirect to /sign-in?error=<SignInError>; a request with Accept: application/json " +
            'gets 403 not_provisioned as JSON instead.',
        access: { auth: 'none', note: 'Anyone (Google redirects here).' },
        query: GoogleCallbackQuery,
        success: {
            status: 302,
            description: 'Signed in — redirect into the app',
            headers: {
                'Set-Cookie': 'ops_sid=<opaque>; HttpOnly; Secure; SameSite=Lax; Path=/; Max-Age=43200',
                Location: 'redirect path or home_route; /sign-in?error=<SignInError> on failure',
            },
        },
        errors: ['not_provisioned', 'validation_failed', 'upstream_unavailable'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'auth.devSignIn': defineRoute({
        id: 'auth.devSignIn',
        method: 'POST',
        path: `${API_BASE}/auth/dev/sign-in`,
        summary: 'Sign in without Google (local and test environments only)',
        description: 'NOT REGISTERED IN PRODUCTION. Enabled only when NODE_ENV != production AND AUTH_DEV_LOGIN=1. Same active-user ' +
            'check as Google sign-in; used by local runs and the per-role test matrix (ADR §6.6).',
        access: { auth: 'none', note: 'Anyone, but only where the route exists.' },
        body: DevSignInBody,
        success: {
            status: 204,
            description: 'Signed in',
            headers: { 'Set-Cookie': 'ops_sid=<opaque>; HttpOnly; SameSite=Lax; Path=/; Max-Age=43200' },
        },
        errors: ['not_provisioned', 'validation_failed'],
        idempotency: 'none',
        rateLimit: 'write',
        devOnly: true,
    }),
    'auth.passwordSignIn': defineRoute({
        id: 'auth.passwordSignIn',
        method: 'POST',
        path: `${API_BASE}/auth/password/sign-in`,
        summary: 'Sign in with email and password',
        description: 'The second sign-in method beside Google (operator decision 17 Oct 2026), registered in every environment, ' +
            'production included. Same active-user check and the same ops_sid session as Google sign-in. 401 ' +
            'invalid_credentials, with one message, for a wrong password, an unknown email, a disabled person and an account ' +
            'with no password. Passwords are argon2id hashes, compared in constant time and never logged or returned. ' +
            '429 rate_limited per IP and per email from one IP. Lockout (db/policies/0077), for a known or an unknown ' +
            'email alike: an address is an IPv4 address or an IPv6 /64; after 10 failed sign-ins for one email from one ' +
            'address inside 15 minutes, that email is locked for 15 minutes from that address; after 50 for one email from ' +
            'any addresses inside 15 minutes, EVERY address is refused for that email except a known-good one (an address ' +
            'that signed in to it in the last 30 days with its current password). A temporary password (a manager’s reset, the founder’s bootstrap) signs in with the session ' +
            'flagged: GET /me says must_change_password, and every other session route answers 403 must_change_password ' +
            'until POST /auth/password/change. Every success and failure is audited.',
        access: { auth: 'none', note: 'Anyone.' },
        body: PasswordSignInBody,
        success: {
            status: 204,
            description: 'Signed in',
            headers: { 'Set-Cookie': 'ops_sid=<opaque>; HttpOnly; Secure; SameSite=Lax; Path=/; Max-Age=43200' },
        },
        errors: ['invalid_credentials', 'validation_failed', 'rate_limited'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'auth.passwordChange': defineRoute({
        id: 'auth.passwordChange',
        method: 'POST',
        path: `${API_BASE}/auth/password/change`,
        summary: 'Change your own password',
        description: 'In your own name only (403 while acting or viewing as someone). current_password is the one you signed in with ' +
            '(the temporary one after a reset); new_password is 10–256 characters and not the current one. Clears ' +
            'must_change_password and ends your other sessions; this one stays signed in. 401 invalid_credentials on a ' +
            'wrong current_password (it counts towards the lockout, 429 once locked); 409 conflict when you have no ' +
            'password (Google only — ask a manager for one). Audited.',
        access: { auth: 'session', note: 'Any signed-in user, for themself. Allowed while must_change_password.' },
        body: PasswordChangeBody,
        success: { status: 204, description: 'Changed' },
        errors: ['no_session', 'invalid_credentials', 'forbidden', 'validation_failed', 'conflict', 'rate_limited'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'auth.logout': defineRoute({
        id: 'auth.logout',
        method: 'POST',
        path: `${API_BASE}/auth/logout`,
        summary: 'Sign out',
        description: 'Deletes the session row and clears the cookie. Always 204, also when there was no session.',
        access: { auth: 'none', note: 'Anyone; acts on the caller’s own session only.' },
        success: { status: 204, description: 'Signed out', headers: { 'Set-Cookie': 'ops_sid=; Max-Age=0; Path=/' } },
        errors: [],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- me
    'me.get': defineRoute({
        id: 'me.get',
        method: 'GET',
        path: `${API_BASE}/me`,
        summary: 'Who am I: user, capabilities, menu, home route, gate, flags',
        description: 'The no-hard-wired-people contract. The SPA renders its navigation, buttons and home route from this response only.',
        access: { auth: 'session', note: 'Any signed-in user.' },
        success: { status: 200, description: 'The signed-in user’s view of the app', schema: MeResponse },
        errors: ['no_session'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ---------------------------------------------------------------- users
    'users.list': defineRoute({
        id: 'users.list',
        method: 'GET',
        path: `${API_BASE}/users`,
        summary: 'List people (Settings → People)',
        access: { auth: 'session', anyOf: ['users.read'], note: 'Managers. Others get 403 forbidden.' },
        query: ListUsersQuery,
        success: { status: 200, description: 'A page of users, by name', schema: UserPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'users.create': defineRoute({
        id: 'users.create',
        method: 'POST',
        path: `${API_BASE}/users`,
        summary: 'Add a person with a role',
        description: 'Sends no invite (no email in v1); returns sign_in_url to share. Email must be on the allowed sign-in domain ' +
            '(422 otherwise). 409 conflict when the email already belongs to a user. 403 forbidden when anyone but a ' +
            'founder (in their own name, not acting as someone) adds a founder; the database refuses it too.',
        access: { auth: 'session', anyOf: ['users.write'], note: 'Managers. Others get 403 forbidden.' },
        headers: IdempotencyHeaders,
        body: CreateUserBody,
        success: {
            status: 201,
            description: 'Created',
            schema: CreateUserResponse,
            headers: { Location: '/api/v1/users/{id}', ...ETAG },
        },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'users.update': defineRoute({
        id: 'users.update',
        method: 'PATCH',
        path: `${API_BASE}/users/{id}`,
        summary: 'Change a person’s details, role or status',
        description: 'Role changes take effect on the user’s next request. status=disabled deletes all their sessions in the same ' +
            'transaction. 409 conflict when the change would leave no active founder, or when a user disables themself. ' +
            '403 forbidden (field_errors on role or status) when: the caller changes their OWN role (any role); a non-founder ' +
            'grants or removes the founder role, or disables or re-enables a founder; or the caller is acting or viewing as ' +
            'someone. The AD manages every non-founder role. When a non-founder gives someone a role outside their password ' +
            'ladder (anything but ops_member, sales or marketing), that person’s password is removed (Google sign-in only; a ' +
            'founder can issue a new one) and all their sessions end, in the same transaction, so a role change cannot carry ' +
            'a password past the ladder. Enforced again by database triggers; every role change and password removal is ' +
            'audited (actor, acting-as user, before, after; never a hash).',
        access: { auth: 'session', anyOf: ['users.write'], deny: 'forbidden', note: 'Managers. Others get 403 forbidden.' },
        params: UserIdParams,
        headers: IfMatchHeaders,
        body: UpdateUserBody,
        success: { status: 200, description: 'Updated', schema: User, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'users.grantCapability': defineRoute({
        id: 'users.grantCapability',
        method: 'PUT',
        path: `${API_BASE}/users/{id}/capabilities/{capability}`,
        summary: 'Grant a capability to one person, on top of their role',
        description: 'Operator decisions 11–12: `holidays.manage` (ADR-005’s hr.admin) is carried by the hr role AND grantable to anyone; ' +
            'the founder holds it at go-live. The grant gives that capability only — no client, task, timesheet, usage or ' +
            'revenue sight (ADR-005 §2). Takes effect on the person’s next request. Granting one they already hold changes ' +
            'nothing (200). 403 forbidden when the caller changes their OWN capabilities, a non-founder changes a founder’s, ' +
            'or the caller is acting or viewing as someone. 409 conflict when the person is disabled. Audited.',
        access: { auth: 'session', anyOf: ['users.write'], deny: 'forbidden', note: 'Managers (Settings → People). Others get 403 forbidden.' },
        params: UserCapabilityParams,
        success: { status: 200, description: 'The person, with granted_capabilities', schema: User, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'users.revokeCapability': defineRoute({
        id: 'users.revokeCapability',
        method: 'DELETE',
        path: `${API_BASE}/users/{id}/capabilities/{capability}`,
        summary: 'Take back a capability granted to one person',
        description: 'Ends the grant (kept in history with who revoked it and when); access ends on the person’s next request. ' +
            'Revoking one they do not hold changes nothing (200). A role’s own capability (e.g. the hr role’s ' +
            'holidays.manage) is not a grant and is not removed. Same 403 rules as granting. Audited.',
        access: { auth: 'session', anyOf: ['users.write'], deny: 'forbidden', note: 'Managers (Settings → People). Others get 403 forbidden.' },
        params: UserCapabilityParams,
        success: { status: 200, description: 'The person, with granted_capabilities', schema: User, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'users.rotateCaptureToken': defineRoute({
        id: 'users.rotateCaptureToken',
        method: 'POST',
        path: `${API_BASE}/users/{id}/capture-token/rotate`,
        summary: 'Issue a new personal capture link (shown once)',
        description: 'Invalidates the previous link. A non-manager asking for someone else’s id gets 404.',
        access: {
            auth: 'session',
            note: 'Managers for anyone; any signed-in user for their own id (sales, marketing and hr excepted — they have no capture).',
        },
        params: UserIdParams,
        success: { status: 200, description: 'The new link', schema: CaptureTokenResponse },
        errors: ['no_session', 'not_found', 'forbidden'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'users.passwordReset': defineRoute({
        id: 'users.passwordReset',
        method: 'POST',
        path: `${API_BASE}/users/{id}/password-reset`,
        summary: 'Give a person a new temporary password (shown once)',
        description: 'Settings → People. Returns a new random temporary password once — it is stored only as a hash. The person ' +
            'must change it at their next sign-in (must_change_password). Ends all their sessions and clears a lockout on ' +
            'their email. Follows the act-as ladder, because a temporary password lets you sign in as them: an ops_ad ' +
            'manages only an ops_member’s, sales’ or marketing’s password, never a peer ops_ad’s, HR’s or a founder’s; a ' +
            'founder manages anyone’s (the recovery path for a founder or HR). 403 forbidden outside that, for your own ' +
            '(use POST /auth/password/change), and while acting or viewing as someone; 409 conflict when the person is ' +
            'disabled. Audited (never the password).',
        access: { auth: 'session', anyOf: ['users.write'], deny: 'forbidden', note: 'Managers (Settings → People). Others get 403 forbidden.' },
        params: UserIdParams,
        success: { status: 200, description: 'The temporary password and the person', schema: PasswordResetResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'users.passwordRemove': defineRoute({
        id: 'users.passwordRemove',
        method: 'DELETE',
        path: `${API_BASE}/users/{id}/password`,
        summary: 'Remove a person’s password (Google sign-in only from then on)',
        description: '204, also when they had none. Ends all their sessions. The same 403 rules as password-reset. Audited.',
        access: { auth: 'session', anyOf: ['users.write'], deny: 'forbidden', note: 'Managers (Settings → People). Others get 403 forbidden.' },
        params: UserIdParams,
        success: { status: 204, description: 'Removed' },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- clients
    'clients.list': defineRoute({
        id: 'clients.list',
        method: 'GET',
        path: `${API_BASE}/clients`,
        summary: 'All clients (Clients → All clients)',
        description: 'Managers get every client; an ops_member gets only clients they are the current SPOC of. annual_revenue_lakh ' +
            'is present only for callers with revenue.read.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers → all; ops_member → own. Requesters: 403.' },
        query: ListClientsQuery,
        success: {
            status: 200,
            description: 'A page of client rows',
            schema: ClientRowPage,
            byAudience: { revenue: page(ClientRowManager), other: page(ClientRowMember) },
        },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clients.create': defineRoute({
        id: 'clients.create',
        method: 'POST',
        path: `${API_BASE}/clients`,
        summary: 'Add a client with its SPOC',
        description: 'Opens the first client_spocs row for spoc_id (reason "Client added"). spoc_id is required (422 without it) ' +
            'and must be an active ops_ad or ops_member (422 otherwise).',
        access: { auth: 'session', anyOf: ['clients.create'], note: 'Managers. Others get 403 forbidden.' },
        headers: IdempotencyHeaders,
        body: CreateClientBody,
        success: {
            status: 201,
            description: 'Created',
            schema: ClientManager,
            headers: { Location: '/api/v1/clients/{id}', ...ETAG },
        },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'clients.get': defineRoute({
        id: 'clients.get',
        method: 'GET',
        path: `${API_BASE}/clients/{id}`,
        summary: 'One client with contacts, products and priority suggestion',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the current SPOC. Anyone else: 404.' },
        params: ClientIdParams,
        success: {
            status: 200,
            description: 'The client',
            schema: ClientDetail,
            byAudience: { revenue: ClientDetailManager, other: ClientDetailMember },
            headers: ETAG,
        },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clients.update': defineRoute({
        id: 'clients.update',
        method: 'PATCH',
        path: `${API_BASE}/clients/{id}`,
        summary: 'Edit a client',
        description: 'Managers: any field. ops_member on their own client: name, segment, monthly_hours only — sending a ' +
            'manager-only field (annual_revenue_lakh, priority, strategic, priority_note, status) is 403 forbidden with ' +
            'field_errors naming it. A priority change also writes a client note.',
        access: { auth: 'session', anyOf: CLIENT_EDIT, note: 'Managers, or the current SPOC (limited fields). Others: 404.' },
        params: ClientIdParams,
        headers: IfMatchHeaders,
        body: UpdateClientBody,
        success: {
            status: 200,
            description: 'Updated',
            schema: Client,
            byAudience: { revenue: ClientManager, other: ClientMember },
            headers: ETAG,
        },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- contacts
    'contacts.list': defineRoute({
        id: 'contacts.list',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/contacts`,
        summary: 'A client’s contacts, primary first',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the current SPOC. Others: 404.' },
        params: ClientIdParams,
        success: { status: 200, description: 'Contacts', schema: ClientContactList },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'contacts.create': defineRoute({
        id: 'contacts.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/contacts`,
        summary: 'Add a contact to a client',
        access: { auth: 'session', anyOf: CLIENT_EDIT, note: 'Managers, or the current SPOC. Others: 404.' },
        params: ClientIdParams,
        headers: IdempotencyHeaders,
        body: CreateContactBody,
        success: { status: 201, description: 'Created', schema: ClientContact, headers: ETAG },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'contacts.update': defineRoute({
        id: 'contacts.update',
        method: 'PATCH',
        path: `${API_BASE}/clients/{id}/contacts/{contact_id}`,
        summary: 'Edit a client contact',
        access: { auth: 'session', anyOf: CLIENT_EDIT, note: 'Managers, or the current SPOC. Others: 404.' },
        params: ContactParams,
        headers: IfMatchHeaders,
        body: UpdateContactBody,
        success: { status: 200, description: 'Updated', schema: ClientContact, headers: ETAG },
        errors: ['no_session', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- email domains
    'emailDomains.list': defineRoute({
        id: 'emailDomains.list',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/email-domains`,
        summary: 'Email domains that identify this client’s mail',
        access: {
            auth: 'session',
            anyOf: CLIENT_READ,
            action: ['client.emailDomains.write'],
            note: 'Managers. The SPOC (who can see the client) gets 403; anyone else 404.',
        },
        params: ClientIdParams,
        success: { status: 200, description: 'Domains', schema: ClientEmailDomainList },
        errors: ['no_session', 'not_found', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'emailDomains.create': defineRoute({
        id: 'emailDomains.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/email-domains`,
        summary: 'Link an email domain to a client',
        description: '409 conflict when the domain is already linked to a client (domains are unique across clients).',
        access: {
            auth: 'session',
            anyOf: CLIENT_READ,
            action: ['client.emailDomains.write'],
            note: 'Managers. The SPOC gets 403; anyone else 404.',
        },
        params: ClientIdParams,
        headers: IdempotencyHeaders,
        body: CreateEmailDomainBody,
        success: { status: 201, description: 'Linked', schema: ClientEmailDomain },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'emailDomains.delete': defineRoute({
        id: 'emailDomains.delete',
        method: 'DELETE',
        path: `${API_BASE}/clients/{id}/email-domains/{domain_id}`,
        summary: 'Unlink an email domain',
        access: {
            auth: 'session',
            anyOf: CLIENT_READ,
            action: ['client.emailDomains.write'],
            note: 'Managers. The SPOC gets 403; anyone else 404.',
        },
        params: EmailDomainParams,
        success: { status: 204, description: 'Unlinked' },
        errors: ['no_session', 'not_found', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- SPOC
    'spocs.history': defineRoute({
        id: 'spocs.history',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/spocs`,
        summary: 'Full dated SPOC history with reasons, newest first',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the current SPOC. Others: 404.' },
        params: ClientIdParams,
        success: { status: 200, description: 'History', schema: SpocHistory },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'spocs.change': defineRoute({
        id: 'spocs.change',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/spoc`,
        summary: 'Change the SPOC, with a reason',
        description: 'One transaction: closes the open client_spocs row, opens one for user_id, optionally moves open tasks ' +
            '(an assignment-log entry each) and writes a spoc_change client note. 422 when user_id is not an active ' +
            'ops_ad/ops_member; 409 when user_id is already the SPOC. Optional If-Match on the client version.',
        access: {
            auth: 'session',
            anyOf: CLIENT_READ,
            action: ['client.changeSpoc'],
            note: 'Founder and Ops associate director (operator decision). The SPOC gets 403; anyone else 404.',
        },
        params: ClientIdParams,
        headers: IdempotencyHeaders.extend(OptionalIfMatchHeaders.shape),
        body: ChangeSpocBody,
        success: { status: 200, description: 'SPOC changed', schema: ChangeSpocResponse },
        preconditionCurrent: Client,
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'key',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- notes
    'clientNotes.list': defineRoute({
        id: 'clientNotes.list',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/notes`,
        summary: 'Client notes, newest first',
        access: { auth: 'session', anyOf: CLIENT_READ, note: 'Managers, or the current SPOC. Others: 404.' },
        params: ClientIdParams,
        query: ListClientNotesQuery,
        success: { status: 200, description: 'A page of notes', schema: ClientNotePage },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ---------------------------------------------------------------- audit
    'audit.list': defineRoute({
        id: 'audit.list',
        method: 'GET',
        path: `${API_BASE}/audit`,
        summary: 'The audit trail, newest first',
        access: { auth: 'session', anyOf: ['audit.read'], note: 'Managers. Others get 403 forbidden.' },
        query: ListAuditQuery,
        success: { status: 200, description: 'A page of audit entries', schema: AuditPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'audit.rowHistory': defineRoute({
        id: 'audit.rowHistory',
        method: 'GET',
        path: `${API_BASE}/audit/{table}/{row_id}`,
        summary: 'One row’s full history, newest first (the History tab of side panels)',
        description: 'Allowed for managers and for anyone who may read that row under RLS; otherwise 404. Revenue columns are ' +
            'removed from before/after/changed for callers without revenue.read.',
        access: { auth: 'session', note: 'Managers, or anyone who may read the row. Others: 404.' },
        params: RowHistoryParams,
        query: RowHistoryQuery,
        success: {
            status: 200,
            description: 'A page of audit entries for the row',
            schema: AuditPage,
            byAudience: { revenue: AuditPage, other: AuditPageMember },
        },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ---------------------------------------------------------------- system
    'system.health': defineRoute({
        id: 'system.health',
        method: 'GET',
        path: `${API_BASE}/health`,
        summary: 'Liveness and database probe',
        access: { auth: 'none', note: 'Anyone. Returns no data.' },
        success: { status: 200, description: 'Up (ok=false and db=down returns 503 with the same body)', schema: HealthResponse },
        errors: ['upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
};
//# sourceMappingURL=routes.step1.js.map